import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { LayoutDashboard, FileText, Carrot, ChefHat, Bell, Sparkles, Moon, Sun, LogOut, Menu, Settings, Users, MapPin, BookOpen } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { t } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { QuotaBanner } from "@/components/billing/QuotaBanner";

const NAV = [
  { to: "/dashboard", label: t("appLayout.tableau_de_bord"), icon: LayoutDashboard, id: "dashboard" },
  { to: "/ma-carte", label: t("common.ma_carte"), icon: BookOpen, id: "my-menu" },
  { to: "/invoices", label: t("common.factures"), icon: FileText, id: "invoices" },
  { to: "/ingredients", label: t("common.ingredients"), icon: Carrot, id: "ingredients" },
  { to: "/recipes", label: t("common.recettes"), icon: ChefHat, id: "recipes" },
  { to: "/alerts", label: t("common.alertes_prix"), icon: Bell, id: "alerts" },
  { to: "/copilot", label: t("common.copilote_ia"), icon: Sparkles, id: "copilot" },
  { to: "/settings", label: t("common.parametres"), icon: Settings, id: "settings" },
];

function NavItems({ unread, onNavigate }) {
  const { user } = useAuth();
  const items = user?.role === "admin" ? [...NAV, { to: "/admin/users", label: t("appLayout.utilisateurs"), icon: Users, id: "admin-users" }, { to: "/admin/visitors", label: t("common.visiteurs"), icon: MapPin, id: "admin-visitors" }] : NAV;
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, id }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${id}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors lg:py-2 ${
              isActive ? "bg-foreground/[0.06] text-foreground" : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
            }`
          }
        >
          <Icon className={`h-4 w-4 ${id === "copilot" ? "text-[hsl(var(--ai))]" : ""}`} />
          <span className="flex-1">{label}</span>
          {id === "alerts" && unread > 0 && (
            <span data-testid="nav-alerts-unread" className="num rounded-full bg-rose-500/15 px-1.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  return (
    <div className="space-y-3 border-t pt-4">
      <div className="px-1">
        <p className="truncate text-sm font-semibold" data-testid="sidebar-restaurant-name">{user?.restaurant?.name}</p>
        <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
      </div>
      <LanguageSwitcher className="mx-1" />
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" data-testid="theme-toggle-btn" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          <span className="ml-2">{theme === "dark" ? t("appLayout.clair") : t("appLayout.sombre")}</span>
        </Button>
        <Button variant="outline" size="sm" data-testid="logout-btn" onClick={logout} aria-label={t("appLayout.deconnexion")}>
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

const TABS = [
  { to: "/dashboard", label: t("appLayout.accueil"), icon: LayoutDashboard, id: "dashboard" },
  { to: "/invoices", label: t("common.factures"), icon: FileText, id: "invoices" },
  { to: "/recipes", label: t("common.recettes"), icon: ChefHat, id: "recipes" },
  { to: "/copilot", label: t("appLayout.copilote"), icon: Sparkles, id: "copilot" },
];

function BottomNav({ unread, onMore }) {
  const tab = "relative flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors";
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" data-testid="mobile-bottom-nav">
      {TABS.map(({ to, label, icon: Icon, id }) => (
        <NavLink key={to} to={to} data-testid={`tab-${id}`}
          className={({ isActive }) => `${tab} ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-emerald-500" />}
              <Icon className={`h-5 w-5 ${id === "copilot" && isActive ? "text-[hsl(var(--ai))]" : ""}`} />
              {label}
            </>
          )}
        </NavLink>
      ))}
      <button type="button" onClick={onMore} className={`${tab} text-muted-foreground`} data-testid="tab-more">
        <Menu className="h-5 w-5" />
        {t("appLayout.plus")}
        {unread > 0 && <span className="absolute right-[calc(50%-18px)] top-1.5 h-2 w-2 rounded-full bg-rose-500" data-testid="tab-more-unread-dot" />}
      </button>
    </nav>
  );
}

export default function AppLayout() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const refresh = () => api.get("/alerts").then(({ data }) => setUnread(data.filter((a) => !a.read).length)).catch(() => {});
    refresh();
    window.addEventListener("alerts:changed", refresh);
    return () => window.removeEventListener("alerts:changed", refresh);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r bg-card/50 px-4 py-5 lg:flex">
        <Logo className="mb-8 px-1" />
        <div className="flex-1"><NavItems unread={unread} /></div>
        <SidebarFooter />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/80 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] backdrop-blur-md lg:hidden">
        <Logo />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="relative" data-testid="mobile-menu-btn" aria-label={t("appLayout.menu")}>
              <Menu className="h-4 w-4" />
              {unread > 0 && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-rose-500" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-72 flex-col pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-[calc(1.5rem+env(safe-area-inset-top))]">
            <SheetTitle className="sr-only">{t("appLayout.navigation")}</SheetTitle>
            <Logo className="mb-6" />
            <div className="flex-1"><NavItems unread={unread} onNavigate={() => setOpen(false)} /></div>
            <SidebarFooter />
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 lg:px-10 lg:py-10">
          <QuotaBanner />
          <Outlet />
        </div>
      </main>
      <BottomNav unread={unread} onMore={() => setOpen(true)} />
    </div>
  );
}
